import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useExerciseHistory } from "../hooks/useExerciseHistory";
import PrestatieBlok from "../components/PrestatieBlok";
import { saveActiveTraining, getActiveTraining, clearActiveTraining } from "../lib/activeTraining";
import {
  ArrowLeft, Check, Timer, Dumbbell, CheckCircle, X, Info,
  TrendingUp, TrendingDown, Activity, Link, Trophy, Play, Square,
  ClipboardList, FileText, RefreshCw, Search, Plus,
} from "lucide-react";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

function extractYouTubeId(url = "") {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function VideoModal({ videoId, naam, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/95 flex flex-col items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3 px-1">
          <p className="text-white font-semibold text-sm truncate pr-4">{naam}</p>
          <button onClick={onClose} className="text-zinc-400 hover:text-white shrink-0"><X size={20} /></button>
        </div>
        <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingTop: "56.25%" }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title={naam}
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        </div>
        <p className="text-zinc-600 text-xs text-center mt-3">Tik buiten de video om te sluiten</p>
      </div>
    </div>
  );
}

function parseRustSeconds(rustStr) {
  if (!rustStr) return 90;
  const s = rustStr.trim();
  // 1. "X min Y sec" — meest specifiek eerst
  const minSecMatch = s.match(/(\d+)\s*min\w*\s+(\d+)\s*s/i);
  if (minSecMatch) return parseInt(minSecMatch[1]) * 60 + parseInt(minSecMatch[2]);
  // 2. "X:YY" formaat (bijv. "1:30" = 90s, "2:00" = 120s)
  const colonMatch = s.match(/^(\d+):(\d{2})$/);
  if (colonMatch) return parseInt(colonMatch[1]) * 60 + parseInt(colonMatch[2]);
  // 3. Decimale minuten: "1.5 min" = 90s
  const decMinMatch = s.match(/(\d+\.\d+)\s*min/i);
  if (decMinMatch) return Math.round(parseFloat(decMinMatch[1]) * 60);
  // 4. Hele minuten: "2 min" = 120s
  const minMatch = s.match(/(\d+)\s*min/i);
  if (minMatch) return parseInt(minMatch[1]) * 60;
  // 5. Seconden: "90 sec" of "45sec"
  const secMatch = s.match(/(\d+)\s*sec/i);
  if (secMatch) return parseInt(secMatch[1]);
  // 6. Enkel getal (seconden)
  const numMatch = s.match(/^(\d+)$/);
  if (numMatch) return parseInt(numMatch[1]);
  return 90;
}

function formatTime(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ─── GEDEELDE AUDIO CONTEXT (iOS-compatible singleton) ───────────────────────
// iOS Safari blokkeert audio vanuit timer-callbacks tenzij de AudioContext
// eerder is ontgrendeld via een gebruikersklik. Daarom: één gedeelde context
// die bij elke interactie wordt geresumed, zodat timer-callbacks hem wel kunnen gebruiken.
let _sharedAudio = null;

function getAudio() {
  if (!_sharedAudio) {
    try {
      _sharedAudio = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) { return null; }
  }
  return _sharedAudio;
}

// Aanroepen bij ELKE gebruikersklik — ontgrendelt de context voor iOS
function unlockAudio() {
  try {
    const ctx = getAudio();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  } catch (e) {}
}

function playDoorswissel() {
  try {
    const ctx = getAudio();
    if (!ctx) return;
    const sweep = ctx.createOscillator();
    const sweepGain = ctx.createGain();
    sweep.connect(sweepGain); sweepGain.connect(ctx.destination);
    sweep.type = "sine";
    sweep.frequency.setValueAtTime(350, ctx.currentTime);
    sweep.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.18);
    sweepGain.gain.setValueAtTime(0.25, ctx.currentTime);
    sweepGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
    sweep.start(ctx.currentTime); sweep.stop(ctx.currentTime + 0.22);
    const ding = ctx.createOscillator();
    const dingGain = ctx.createGain();
    ding.connect(dingGain); dingGain.connect(ctx.destination);
    ding.type = "sine";
    ding.frequency.value = 1400;
    dingGain.gain.setValueAtTime(0.3, ctx.currentTime + 0.18);
    dingGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.38);
    ding.start(ctx.currentTime + 0.18); ding.stop(ctx.currentTime + 0.38);
  } catch (e) {}
}

function playRestDoneSound() {
  try {
    const ctx = getAudio();
    if (!ctx) return;
    [[0, 660], [0.18, 660], [0.36, 880]].forEach(([delay, freq]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.35, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.14);
      osc.start(ctx.currentTime + delay); osc.stop(ctx.currentTime + delay + 0.14);
    });
  } catch (e) {}
}

// ─── GOLD TOOLTIP ─────────────────────────────────────────────────────────────
function GoldTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border px-3 py-2 text-xs"
      style={{ background: "#111", borderColor: "rgba(201,168,76,0.25)" }}>
      <p className="text-zinc-400 mb-1">{label}</p>
      <p className="font-bold" style={{ color: GOLD }}>{payload[0].value?.toLocaleString("nl-NL")} kg</p>
    </div>
  );
}

// ─── CONDITIE INVOER TYPE ─────────────────────────────────────────────────────
// Detecteert op basis van het "reps" veld welk soort invoer een conditie-oefening heeft.
function parseConditieType(repsStr) {
  if (!repsStr) return { label: "Reps", placeholder: "10", inputMode: "numeric", isTime: false };
  const s = String(repsStr).toLowerCase().trim();
  if (s.includes("min") || s.includes("sec") || /^\d+:\d{2}$/.test(s))
    return { label: "Tijd", placeholder: "0:00", inputMode: "text", isTime: true };
  if (s.includes("km"))
    return { label: "Afstand", placeholder: "0.0 km", inputMode: "decimal", isTime: false };
  if (s.match(/\d+\s*m$/) || s.endsWith("m ") || s === "m")
    return { label: "Afstand", placeholder: "0 m", inputMode: "numeric", isTime: false };
  if (s.includes("cal"))
    return { label: "Cal", placeholder: "0", inputMode: "numeric", isTime: false };
  return { label: "Reps", placeholder: s, inputMode: "numeric", isTime: false };
}

// ─── TIJDSHULP FUNCTIES ───────────────────────────────────────────────────────
// Formatteert seconden naar "m:ss" weergave (bijv. 75 → "1:15")
function formatSeconds(s) {
  const abs = Math.max(0, s);
  return `${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
}

// Parseert een tijdstring naar seconden (bijv. "1 min" → 60, "45 sec" → 45, "1:30" → 90)
function parseTargetSeconds(repsStr) {
  if (!repsStr) return null;
  const s = String(repsStr).toLowerCase().trim();
  const minMatch = s.match(/^(\d+(?:\.\d+)?)\s*min/);
  if (minMatch) return Math.round(parseFloat(minMatch[1]) * 60);
  const secMatch = s.match(/^(\d+(?:\.\d+)?)\s*s(?:ec)?/);
  if (secMatch) return Math.round(parseFloat(secMatch[1]));
  const colonMatch = s.match(/^(\d+):(\d{2})$/);
  if (colonMatch) return parseInt(colonMatch[1]) * 60 + parseInt(colonMatch[2]);
  return null;
}

// ─── PR MELDING TOAST ─────────────────────────────────────────────────────────
function PrMelding({ melding, onDismiss }) {
  useEffect(() => {
    if (!melding) return;
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [melding]);

  if (!melding) return null;

  return (
    <div
      className="fixed top-4 left-0 right-0 z-[300] flex justify-center px-4 pointer-events-none"
      data-testid="pr-melding-toast"
    >
      <button
        onClick={onDismiss}
        className="pointer-events-auto flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border transition-transform active:scale-95"
        style={{
          background: "#0d0d0d",
          borderColor: "#C9A84C",
          boxShadow: "0 0 50px rgba(201,168,76,0.4)",
        }}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "#C9A84C" }}
        >
          <Trophy size={18} className="text-black" />
        </div>
        <div className="text-left">
          <p style={{ color: "#C9A84C" }} className="font-black text-sm leading-tight">
            Nieuw persoonlijk record!
          </p>
          <p className="text-white text-xs mt-0.5 font-semibold">
            {melding.naam} — {melding.kg} kg
          </p>
        </div>
      </button>
    </div>
  );
}

// ─── CUSTOM COMPLETION SCREEN ─────────────────────────────────────────────────
function CustomCompletionScreen({ elapsed, dagNaam, schemaId, stableSessionId, savedExercises, userToken, sessionPrs, onBack, onAccount }) {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (!userToken) return;
    fetch(`${API}/api/workout/${schemaId}/${encodeURIComponent(stableSessionId)}/history`, {
      headers: { Authorization: `Bearer ${userToken}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setHistory(d.logs || []))
      .catch(() => setHistory([]));
  }, [schemaId, stableSessionId, userToken]);

  const totalVolume = savedExercises.reduce((sum, ex) =>
    sum + ex.sets.reduce((s2, s) => s2 + (parseFloat(s.kg) || 0) * (parseInt(s.reps) || 0), 0), 0);
  const completedSets = savedExercises.reduce((sum, ex) => sum + ex.sets.filter(s => s.completed).length, 0);
  const totalSets = savedExercises.reduce((sum, ex) => sum + ex.sets.length, 0);

  // Grafiekdata per oefening op basis van volledige history
  const chartData = history
    ? savedExercises.map(ex => {
        const points = history.map(log => {
          const logEx = log.exercises?.find(e => e.naam === ex.naam);
          const vol = logEx
            ? logEx.sets.reduce((s, set) => s + (parseFloat(set.kg) || 0) * (parseInt(set.reps) || 0), 0)
            : 0;
          return {
            datum: new Date(log.logged_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }),
            vol,
          };
        });
        return { naam: ex.naam, points };
      })
    : [];

  return (
    <div className="min-h-screen bg-[#080808] pb-10">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#080808]/95 backdrop-blur-md border-b border-white/5 px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "rgba(201,168,76,0.15)" }}>
          <CheckCircle size={16} style={{ color: GOLD }} />
        </div>
        <div>
          <p className="text-white font-bold text-sm leading-none">Training voltooid!</p>
          <p className="text-zinc-500 text-xs mt-0.5">{dagNaam}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-3 pt-5 space-y-4">
        {/* Stats rij */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Tijd", value: formatTime(elapsed) },
            { label: "Volume", value: totalVolume > 0 ? `${totalVolume.toLocaleString("nl-NL")} kg` : "–" },
            { label: "Sets", value: `${completedSets}/${totalSets}` },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-2xl p-4 text-center border border-white/5" style={{ background: "#111" }}>
              <p className="text-2xl font-bold" style={{ color: GOLD }}>{value}</p>
              <p className="text-zinc-500 text-xs mt-1">{label}</p>
            </div>
          ))}
        </div>

        {/* Nieuwe Records kaart — alleen als er PRs zijn behaald */}
        {sessionPrs?.length > 0 && (
          <div
            className="rounded-2xl border overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0f0c02, #0d0a00)", borderColor: "rgba(201,168,76,0.4)" }}
            data-testid="completion-pr-kaart"
          >
            <div
              className="px-4 py-3 flex items-center gap-2 border-b"
              style={{ borderColor: "rgba(201,168,76,0.2)" }}
            >
              <Trophy size={15} style={{ color: GOLD }} />
              <p className="font-black text-sm" style={{ color: GOLD }}>
                {sessionPrs.length === 1
                  ? "1 nieuw persoonlijk record!"
                  : `${sessionPrs.length} nieuwe persoonlijke records!`}
              </p>
            </div>
            <div className="divide-y divide-white/4">
              {sessionPrs.map((pr, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-black shrink-0"
                      style={{ background: i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : "#CD7F32" }}
                    >
                      {i + 1}
                    </span>
                    <p className="text-white text-sm font-semibold">{pr.naam}</p>
                  </div>
                  <span className="font-black text-sm tabular-nums" style={{ color: GOLD }}>
                    {pr.kg} kg
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Oefeningen overzicht */}
        <div className="rounded-2xl overflow-hidden border border-white/5" style={{ background: "#111" }}>
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <Dumbbell size={13} style={{ color: GOLD }} />
            <p className="text-white font-bold text-sm">Oefeningen</p>
          </div>
          <div className="divide-y divide-white/4">
            {savedExercises.map((ex, i) => {
              const vol = ex.sets.reduce((s, set) =>
                s + (parseFloat(set.kg) || 0) * (parseInt(set.reps) || 0), 0);
              return (
                <div key={i} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white text-sm font-semibold">{ex.naam}</p>
                    {vol > 0 && (
                      <span className="text-xs font-bold" style={{ color: GOLD }}>
                        {vol.toLocaleString("nl-NL")} kg
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ex.sets.map((s, si) => {
                      const kg = parseFloat(s.kg) || 0;
                      const reps = parseInt(s.reps) || 0;
                      return (
                        <div key={si} className="px-2.5 py-1 rounded-lg text-xs font-mono border"
                          style={s.completed
                            ? { background: "rgba(201,168,76,0.1)", color: GOLD, borderColor: "rgba(201,168,76,0.2)" }
                            : { background: "#1a1a1a", color: "#52525b", borderColor: "rgba(255,255,255,0.06)" }}>
                          {kg > 0 ? `${kg} kg × ${reps}` : `× ${reps}`}
                        </div>
                      );
                    })}
                  </div>
                  {ex.notitie?.trim() && (
                    <p className="text-zinc-400 text-xs italic mt-2 flex items-start gap-1.5">
                      <FileText size={11} className="mt-0.5 shrink-0" style={{ color: GOLD }} />
                      <span>{ex.notitie.trim()}</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Voortgangsgrafieken */}
        {history === null ? (
          <div className="rounded-2xl border border-white/5 p-8 text-center" style={{ background: "#111" }}>
            <Activity size={20} className="text-zinc-700 mx-auto mb-2" />
            <p className="text-zinc-600 text-sm">Grafieken laden...</p>
          </div>
        ) : chartData.some(c => c.points.some(p => p.vol > 0)) ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <TrendingUp size={14} style={{ color: GOLD }} />
              <p className="text-white font-bold text-sm">Voortgang per oefening</p>
            </div>
            {chartData.map(({ naam, points }) => {
              const hasVolume = points.some(p => p.vol > 0);
              if (!hasVolume) return null;
              const firstVol = points.find(p => p.vol > 0)?.vol ?? 0;
              const lastVol = [...points].reverse().find(p => p.vol > 0)?.vol ?? 0;
              const isFirstTraining = points.filter(p => p.vol > 0).length < 2;
              const pct = !isFirstTraining && firstVol > 0 ? ((lastVol - firstVol) / firstVol) * 100 : null;
              const maxVol = Math.max(...points.map(p => p.vol), 1);
              const isUp = pct !== null && pct >= 0;

              return (
                <div key={naam} className="rounded-2xl border border-white/5 overflow-hidden" style={{ background: "#111" }}>
                  <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                    <p className="text-white text-sm font-semibold">{naam}</p>
                    {isFirstTraining ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-lg"
                        style={{ background: "rgba(201,168,76,0.1)", color: GOLD }}>
                        Eerste training
                      </span>
                    ) : pct !== null && (
                      <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-lg"
                        style={isUp
                          ? { background: "rgba(74,222,128,0.1)", color: "#4ade80" }
                          : { background: "rgba(248,113,113,0.1)", color: "#f87171" }}>
                        {isUp ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                        {isUp ? "+" : ""}{pct.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <div className="px-2 pb-3">
                    <ResponsiveContainer width="100%" height={130}>
                      <LineChart data={points} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis dataKey="datum" tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, maxVol * 1.15]} tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
                        <Tooltip content={<GoldTooltip />} />
                        <Line type="monotone" dataKey="vol" stroke={GOLD} strokeWidth={2}
                          dot={{ fill: GOLD, r: 4, strokeWidth: 0 }}
                          activeDot={{ fill: GOLD, r: 6, strokeWidth: 0 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Navigatie knoppen */}
        <div className="flex gap-3 pt-2">
          <button onClick={onBack}
            className="flex-1 py-4 rounded-2xl font-bold text-sm border border-white/10 text-zinc-300 hover:bg-white/5 transition-colors">
            Terug naar schema
          </button>
          <button onClick={onAccount}
            className="flex-1 py-4 rounded-2xl font-bold text-sm text-black"
            style={{ background: `linear-gradient(135deg, ${GOLD}, #D4AF37)` }}>
            Mijn voortgang
          </button>
        </div>
      </div>
    </div>
  );
}

function formatRestTime(s) {
  const abs = Math.abs(s);
  return `${Math.floor(abs / 60)}:${(abs % 60).toString().padStart(2, "0")}`;
}

function RestTimer({ endTime, total, onSkip }) {
  const remaining = endTime ? Math.max(0, Math.ceil((endTime - Date.now()) / 1000)) : 0;
  if (!endTime || remaining <= 0) return null;
  const pct = Math.max(0, (remaining / total) * 100);
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0a0802] border-t border-[#C9A84C]/25 px-4 pt-4 pb-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Timer size={16} className="text-[#C9A84C]" />
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A84C]">Rusttijd</span>
          </div>
          <button onClick={onSkip} className="text-zinc-400 hover:text-white text-sm font-semibold px-3 py-1 rounded-lg border border-white/8 hover:border-white/20 transition-colors">
            Overslaan
          </button>
        </div>
        <div className="flex items-center gap-5">
          <span className="text-5xl font-bold tabular-nums tracking-tight w-28 text-[#C9A84C]">
            {formatRestTime(remaining)}
          </span>
          <div className="flex-1">
            <div className="h-2.5 bg-white/8 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, background: `linear-gradient(90deg, #C9A84C, #D4AF37)` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExerciseInfoModal({ ex, onClose, token, huidigeSets }) {
  const [showVideo, setShowVideo] = useState(false);
  const videoId = extractYouTubeId(ex.video_url);
  const isConditie = ex.spiergroep === "Conditie" || ex.type === "conditie";

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/60" />
        <div
          className="relative w-full max-w-lg rounded-t-2xl p-5 pb-8"
          style={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)", borderBottom: "none" }}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Info size={15} style={{ color: GOLD }} />
              <span className="text-white font-bold text-sm truncate max-w-[240px]">{ex.naam}</span>
            </div>
            <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
          <div className="space-y-3">
            {(ex.sets || ex.reps) && (
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-xs uppercase tracking-widest font-bold w-16 shrink-0">Sets</span>
                <span className="text-zinc-200 text-sm font-semibold">
                  {ex.sets && ex.reps ? `${ex.sets} × ${ex.reps}` : ex.sets || ex.reps}
                </span>
              </div>
            )}
            {ex.rust && (
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-xs uppercase tracking-widest font-bold w-16 shrink-0">Rust</span>
                <span className="text-zinc-200 text-sm font-semibold">{ex.rust}</span>
              </div>
            )}
            {ex.gewicht && (
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-xs uppercase tracking-widest font-bold w-16 shrink-0">Gewicht</span>
                <span className="text-zinc-200 text-sm font-semibold">{ex.gewicht}</span>
              </div>
            )}
            {(ex.notities || ex.instructie) && (
              <div className="pt-3 border-t border-white/6">
                <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold mb-2">Uitvoering</p>
                <p className="text-zinc-300 text-sm leading-relaxed">{ex.notities || ex.instructie}</p>
              </div>
            )}
            {!ex.notities && !ex.instructie && (
              <p className="text-zinc-600 text-sm italic pt-2 border-t border-white/5">
                Geen aanvullende instructies opgegeven voor deze oefening.
              </p>
            )}
            {!isConditie && ex.naam && (
              <PrestatieBlok naam={ex.naam} token={token} huidigeSets={huidigeSets || []} />
            )}
            {/* Video knop */}
            {videoId && (
              <button
                onClick={() => setShowVideo(true)}
                data-testid={`bekijk-video-${ex.naam?.replace(/\s+/g, "-").toLowerCase()}`}
                className="w-full mt-2 flex items-center justify-center gap-2 py-3 rounded-xl border border-red-500/20 bg-red-600/10 text-red-400 hover:bg-red-600/20 transition-colors text-sm font-bold">
                <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center">
                  <Play size={9} fill="white" className="text-white ml-[1px]" />
                </div>
                Bekijk instructievideo
              </button>
            )}
          </div>
        </div>
      </div>
      {showVideo && videoId && (
        <VideoModal videoId={videoId} naam={ex.naam} onClose={() => setShowVideo(false)} />
      )}
    </>
  );
}

// ─── VERVANG / PAS OEFENING AAN MODAL ────────────────────────────────────────
const SPIERGROEPEN_KEUZE = ["Borst", "Rug", "Schouders", "Biceps", "Triceps", "Benen", "Hamstrings", "Billen", "Core", "Kuiten", "Full Body", "Anders"];
const APPARATEN_KEUZE = ["Barbell", "Dumbbell", "Machine", "Kabel", "Bodyweight", "Anders"];

function VervangOefeningModal({ ex, token, onClose, onSave }) {
  const [naam, setNaam] = useState(ex.naam || "");
  const [naamGewijzigd, setNaamGewijzigd] = useState(false);
  const [setsVal, setSetsVal] = useState(ex.sets || "3");
  const [repsVal, setRepsVal] = useState(ex.reps || "10");
  const [rustVal, setRustVal] = useState(ex.rust || "60 sec");
  const [query, setQuery] = useState("");
  const [library, setLibrary] = useState([]);
  const [nieuwForm, setNieuwForm] = useState(null);
  const [nieuwSaving, setNieuwSaving] = useState(false);
  const [nieuwError, setNieuwError] = useState("");

  useEffect(() => {
    fetch(`${API}/api/exercises`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { exercises: [] })
      .then(d => setLibrary(d.exercises || d || []))
      .catch(() => {});
  }, [token]);

  const gefilterd = query.trim()
    ? library.filter(e => e.naam?.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : [];

  const handleSave = () => {
    const nieuw = { sets: setsVal, reps: repsVal, rust: rustVal };
    if (naamGewijzigd && naam.trim() && naam.trim() !== ex.naam) {
      nieuw.naam = naam.trim();
      nieuw.notities = "";
      nieuw.vervangen_van = ex.naam;
    }
    onSave(nieuw);
  };

  const handleNieuwOpslaan = async () => {
    if (!nieuwForm?.naam?.trim()) return;
    setNieuwSaving(true);
    setNieuwError("");
    try {
      const r = await fetch(`${API}/api/exercises`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(nieuwForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
      setLibrary(prev => [...prev, d.exercise]);
      setNaam(d.exercise.naam);
      setNaamGewijzigd(true);
      setQuery("");
      setNieuwForm(null);
    } catch (e) {
      setNieuwError(e.message);
    }
    setNieuwSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-t-3xl max-h-[90vh] flex flex-col"
        style={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)", borderBottom: "none" }}
        onClick={e => e.stopPropagation()} data-testid="vervang-modal">
        <div className="px-5 pt-5 pb-4 border-b border-white/6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw size={15} style={{ color: GOLD }} />
            <div>
              <p className="text-white font-bold text-sm">Oefening aanpassen</p>
              <p className="text-zinc-500 text-xs">Lukt {ex.naam} niet? Vervang of pas aan.</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="vervang-sluiten" className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4 pb-8">
          <div>
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-2">Oefening</p>
            <div className="rounded-xl border border-white/8 px-3 py-2.5 text-sm font-semibold" style={{ background: "#0d0d0d", color: naamGewijzigd ? GOLD : "#fff" }} data-testid="vervang-huidige-naam">
              {naam}{naamGewijzigd && <span className="text-zinc-500 font-normal text-xs ml-2">(was: {ex.naam})</span>}
            </div>
            <div className="relative mt-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Zoek vervangende oefening..."
                data-testid="vervang-zoek-input"
                className="w-full bg-[#0d0d0d] border border-white/8 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600"
              />
            </div>
            {gefilterd.length > 0 && (
              <div className="mt-2 rounded-xl border border-white/8 overflow-hidden" style={{ background: "#0d0d0d" }}>
                {gefilterd.map((e, i) => (
                  <button key={e.id || i}
                    onClick={() => { setNaam(e.naam); setNaamGewijzigd(true); setQuery(""); }}
                    data-testid={`vervang-optie-${i}`}
                    className="w-full text-left px-3 py-2.5 text-sm text-zinc-300 hover:bg-white/5 border-b border-white/4 last:border-0 flex items-center justify-between">
                    <span>{e.naam}</span>
                    {e.spiergroep && <span className="text-zinc-600 text-xs">{e.spiergroep}</span>}
                  </button>
                ))}
              </div>
            )}
            {query.trim() && !nieuwForm && !gefilterd.some(e => e.naam?.toLowerCase() === query.trim().toLowerCase()) && (
              <button
                onClick={() => setNieuwForm({ naam: query.trim(), spiergroep: "Borst", type: "", apparaat: "", uitvoeringstip: "" })}
                data-testid="nieuwe-oefening-btn"
                className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed text-sm font-semibold transition-colors"
                style={{ borderColor: "rgba(201,168,76,0.4)", color: GOLD, background: "rgba(201,168,76,0.05)" }}>
                <Plus size={14} /> "{query.trim()}" toevoegen als nieuwe oefening
              </button>
            )}
            {nieuwForm && (
              <div className="mt-3 rounded-xl border p-3.5 space-y-3" style={{ borderColor: "rgba(201,168,76,0.25)", background: "#0d0d0d" }} data-testid="nieuwe-oefening-form">
                <p className="text-xs font-bold uppercase tracking-widest flex items-center gap-1.5" style={{ color: GOLD }}>
                  <Plus size={11} /> Nieuwe oefening
                </p>
                <div>
                  <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Naam *</p>
                  <input value={nieuwForm.naam}
                    onChange={e => setNieuwForm(f => ({ ...f, naam: e.target.value }))}
                    data-testid="nieuwe-oefening-naam"
                    className="w-full bg-[#111] border border-white/8 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Spiergroep</p>
                    <select value={nieuwForm.spiergroep}
                      onChange={e => setNieuwForm(f => ({ ...f, spiergroep: e.target.value }))}
                      data-testid="nieuwe-oefening-spiergroep"
                      className="w-full bg-[#111] border border-white/8 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50">
                      {SPIERGROEPEN_KEUZE.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Apparaat</p>
                    <select value={nieuwForm.apparaat}
                      onChange={e => setNieuwForm(f => ({ ...f, apparaat: e.target.value }))}
                      data-testid="nieuwe-oefening-apparaat"
                      className="w-full bg-[#111] border border-white/8 rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50">
                      <option value="">— kies —</option>
                      {APPARATEN_KEUZE.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Uitvoeringstip (optioneel)</p>
                  <textarea value={nieuwForm.uitvoeringstip} rows={2}
                    onChange={e => setNieuwForm(f => ({ ...f, uitvoeringstip: e.target.value }))}
                    data-testid="nieuwe-oefening-tip"
                    className="w-full bg-[#111] border border-white/8 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-[#C9A84C]/50"
                    placeholder="bijv. Ellebogen dicht bij het lichaam houden..." />
                </div>
                {nieuwError && <p className="text-red-400 text-xs">{nieuwError}</p>}
                <div className="flex gap-2">
                  <button onClick={() => { setNieuwForm(null); setNieuwError(""); }}
                    className="flex-1 py-2 rounded-lg border border-white/10 text-zinc-400 text-xs font-semibold">
                    Annuleren
                  </button>
                  <button onClick={handleNieuwOpslaan} disabled={nieuwSaving || !nieuwForm.naam.trim()}
                    data-testid="nieuwe-oefening-opslaan"
                    className="flex-1 py-2 rounded-lg text-xs font-bold text-black disabled:opacity-50"
                    style={{ background: `linear-gradient(135deg, ${GOLD}, #D4AF37)` }}>
                    {nieuwSaving ? "Opslaan..." : "Toevoegen & selecteren"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-1.5">Sets</p>
              <input value={setsVal} onChange={e => setSetsVal(e.target.value)} inputMode="numeric" data-testid="vervang-sets-input"
                className="w-full bg-[#0d0d0d] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm text-center focus:outline-none focus:border-[#C9A84C]/50" />
            </div>
            <div>
              <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-1.5">Reps</p>
              <input value={repsVal} onChange={e => setRepsVal(e.target.value)} data-testid="vervang-reps-input"
                className="w-full bg-[#0d0d0d] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm text-center focus:outline-none focus:border-[#C9A84C]/50" />
            </div>
            <div>
              <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-1.5">Rust</p>
              <input value={rustVal} onChange={e => setRustVal(e.target.value)} data-testid="vervang-rust-input"
                className="w-full bg-[#0d0d0d] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm text-center focus:outline-none focus:border-[#C9A84C]/50" />
            </div>
          </div>

          <button onClick={handleSave} data-testid="vervang-opslaan-btn"
            className="w-full py-3.5 rounded-xl font-bold text-sm bg-gradient-to-br from-[#C9A84C] to-[#D4AF37] text-black active:scale-[0.98] transition-transform">
            Toepassen op deze training
          </button>
          <p className="text-zinc-600 text-xs text-center">Na het afronden vragen we of je dit permanent wilt maken.</p>
        </div>
      </div>
    </div>
  );
}

export default function WorkoutCustomLogger() {
  const { schemaId, dagIndex } = useParams();
  const dagIdx = parseInt(dagIndex || "0", 10);
  const { user: authUser } = useAuth();
  const navigate = useNavigate();

  // Ondersteuning voor kiosk modus (PT/Groep): kiosk token heeft prioriteit
  // Gebruik useRef zodat de token stabiel blijft over re-renders heen
  const kioskTokenRef = useRef(sessionStorage.getItem("kiosk_token"));
  const kioskToken = kioskTokenRef.current;
  const effectiveToken = kioskToken || authUser?.token;

  // Ruim kiosk token op uit sessionStorage (eenmalig bij mount)
  useEffect(() => {
    if (kioskTokenRef.current) sessionStorage.removeItem("kiosk_token");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sets, setSets] = useState({});
  const [prevLog, setPrevLog] = useState(null);
  const [restTimer, setRestTimer] = useState(null);
  const [activeConditieTimer, setActiveConditieTimer] = useState(null);
  // { exIdx, setIdx, endTime, total } — actieve aftimer voor één conditie-set
  const [, forceTimerRender] = useState(0);
  const [finished, setFinished] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [savedExercises, setSavedExercises] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [showInfoFor, setShowInfoFor] = useState(null);
  const [showSchema, setShowSchema] = useState(false);
  const [notities, setNotities] = useState({}); // { exIdx: "opmerking" }
  const [vervangingen, setVervangingen] = useState({}); // { exIdx: {naam?, sets, reps, rust} }
  const [showVervangFor, setShowVervangFor] = useState(null);
  const [permanentVraag, setPermanentVraag] = useState(false);
  const [permanentStatus, setPermanentStatus] = useState(null); // null | "saving" | "saved"
  const startTimeRef = useState(() => {
    const s = getActiveTraining();
    return s && s.route === window.location.pathname && s.startedAt ? s.startedAt : Date.now();
  })[0];
  const savedTrainingRef = useRef((() => {
    const s = getActiveTraining();
    return s && s.route === window.location.pathname ? s : null;
  })());
  const restoredRef = useRef(false);

  // Globale oefening-geschiedenis: beste gewicht over alle trainingen heen
  const globalHistory = useExerciseHistory(effectiveToken);
  const globalHistoryRef = useRef(globalHistory);
  useEffect(() => { globalHistoryRef.current = globalHistory; }, [globalHistory]);
  const sessionMaxKg = useRef({}); // Bijhouden van max kg per oefening in deze sessie
  const [prMelding, setPrMelding] = useState(null);
  const [sessionPrs, setSessionPrs] = useState([]); // Alle PRs van deze sessie

  // Stabiele session_id (zonder timestamp) zodat vorige training gevonden wordt
  const stableSessionId = `custom-${schemaId}-dag${dagIdx}`;

  // Laad schema + vorige training parallel (vermijdt race condition)
  useEffect(() => {
    if (!effectiveToken || !schemaId) return;
    setLoading(true);
    setPrevLog(null); // reset bij wisselen van dag
    const sessionId = `custom-${schemaId}-dag${dagIdx}`;
    Promise.all([
      // Probeer eerst persoonlijk schema, dan bedrijfsschema als fallback
      fetch(`${API}/api/my-custom-schemas/${schemaId}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      }).then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) return data;
          // Fallback: bedrijfsschema (voor sporters met toegewezen schema)
          return fetch(`${API}/api/sporter/schemas/${schemaId}`, {
            headers: { Authorization: `Bearer ${effectiveToken}` },
          }).then(r => r.ok ? r.json() : null);
        }),
      fetch(`${API}/api/workout/${schemaId}/${encodeURIComponent(sessionId)}/previous`, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      }).then(r => r.ok ? r.json() : null).catch(() => null),
    ])
      .then(([schemaData, prev]) => {
        setSchema(schemaData);
        if (prev?.exercises) setPrevLog(prev);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [schemaId, dagIdx, effectiveToken]);

  // Rust-timer — loopt door na terugkeer naar de pagina
  useEffect(() => {
    if (!restTimer) return;
    const tick = () => {
      const remaining = Math.ceil((restTimer.endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        playRestDoneSound();
        setRestTimer(null);
      } else {
        forceTimerRender(n => n + 1);
      }
    };
    tick();
    const iv = setInterval(tick, 500);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [restTimer]);

  // Conditie-timer — afteller per set, speelt doorswissel als klaar
  useEffect(() => {
    if (!activeConditieTimer) return;
    const tick = () => {
      const remaining = Math.ceil((activeConditieTimer.endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        playDoorswissel();
        // Markeer de set als klaar en sla de doeltijd op als resultaat
        setSets(prev => {
          const updated = { ...prev };
          const exSets = [...(updated[activeConditieTimer.exIdx] || [])];
          exSets[activeConditieTimer.setIdx] = {
            ...exSets[activeConditieTimer.setIdx],
            done: true,
            weight: formatSeconds(activeConditieTimer.total),
          };
          updated[activeConditieTimer.exIdx] = exSets;
          return updated;
        });
        setActiveConditieTimer(null);
      } else {
        forceTimerRender(n => n + 1);
      }
    };
    tick();
    const iv = setInterval(tick, 100); // 100ms voor soepele weergave
    return () => clearInterval(iv);
  }, [activeConditieTimer]);

  // Initialiseer sets op basis van oefeningen + vorige training gewichten
  useEffect(() => {
    if (!schema) return;
    const dag = schema.dagen?.[dagIdx];
    if (!dag) return;
    if (restoredRef.current) return; // hersteld vanuit actieve training — niet opnieuw initialiseren
    if (savedTrainingRef.current?.state?.sets) {
      setSets(savedTrainingRef.current.state.sets);
      if (savedTrainingRef.current.state.notities) setNotities(savedTrainingRef.current.state.notities);
      if (savedTrainingRef.current.state.vervangingen) setVervangingen(savedTrainingRef.current.state.vervangingen);
      restoredRef.current = true;
      return;
    }
    const init = {};
    dag.oefeningen?.forEach((ex, i) => {
      if (ex.type === "superset") {
        // Per sub-oefening eigen sets: sleutel is `${i}-${si}`
        (ex.oefeningen || []).forEach((subEx, si) => {
          const numSets = subEx.sets ? parseInt(subEx.sets) || 3 : 3;
          const defaultReps = subEx.reps ? parseInt(String(subEx.reps).match(/\d+/)?.[0]) || 10 : 10;
          const prevEx = prevLog?.exercises?.find(
            pe => (subEx.id && pe.code === subEx.id) || pe.naam === subEx.naam
          );
          init[`${i}-${si}`] = Array.from({ length: numSets }, (_, setIdx) => {
            const ps = prevEx?.sets?.[setIdx];
            return {
              reps: ps?.reps != null ? ps.reps : defaultReps,
              weight: ps?.kg != null ? String(ps.kg) : "",
              done: false,
            };
          });
        });
      } else {
        const numSets = ex.sets ? parseInt(ex.sets) || 3 : 3;
        const defaultReps = ex.reps ? parseInt(String(ex.reps).match(/\d+/)?.[0]) || 10 : 10;
        const prevEx = prevLog?.exercises?.find(
          pe => (ex.id && pe.code === ex.id) || pe.naam === ex.naam
        );
        init[i] = Array.from({ length: numSets }, (_, si) => {
          const ps = prevEx?.sets?.[si];
          return {
            reps: ps?.reps != null ? ps.reps : defaultReps,
            weight: ps?.kg != null ? String(ps.kg) : "",
            done: false,
          };
        });
      }
    });
    setSets(init);
  }, [schema, dagIdx, prevLog]);

  // Globale fallback: vul lege gewichten in vanuit globale oefening-geschiedenis
  // Prioriteit: handmatig ingevoerd > prevLog (zelfde schema) > globalHistory (over alle trainingen)
  useEffect(() => {
    if (!schema || Object.keys(globalHistory).length === 0) return;
    const dag = schema.dagen?.[dagIdx];
    if (!dag) return;

    setSets(prev => {
      const updated = { ...prev };
      let changed = false;

      dag.oefeningen?.forEach((ex, i) => {
        if (ex.type === "superset") {
          (ex.oefeningen || []).forEach((subEx, si) => {
            const key = `${i}-${si}`;
            const hasPrevLog = prevLog?.exercises?.some(
              pe => (subEx.id && pe.code === subEx.id) || pe.naam === subEx.naam
            );
            if (hasPrevLog) return;
            const global = globalHistory[subEx.naam];
            if (!global) return;
            const currentSets = updated[key] || [];
            if (currentSets.some(s => s.weight)) return; // handmatig ingevoerd — niet overschrijven
            updated[key] = currentSets.map(s => ({
              ...s,
              weight: global.kg != null ? String(global.kg) : s.weight,
            }));
            changed = true;
          });
        } else {
          const hasPrevLog = prevLog?.exercises?.some(
            pe => (ex.id && pe.code === ex.id) || pe.naam === ex.naam
          );
          if (hasPrevLog) return;
          const global = globalHistory[ex.naam];
          if (!global) return;
          const currentSets = updated[i] || [];
          if (currentSets.some(s => s.weight)) return; // handmatig ingevoerd — niet overschrijven
          updated[i] = currentSets.map(s => ({
            ...s,
            weight: global.kg != null ? String(global.kg) : s.weight,
          }));
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [schema, dagIdx, prevLog, globalHistory]);

  // Notitie-prefill: laatste opmerking bij dezelfde oefening automatisch tonen
  useEffect(() => {
    if (!schema || Object.keys(globalHistory).length === 0) return;
    const dag = schema.dagen?.[dagIdx];
    if (!dag) return;
    setNotities(prev => {
      const next = { ...prev };
      let changed = false;
      dag.oefeningen?.forEach((ex, i) => {
        if (ex.type === "superset") return;
        const note = globalHistory[ex.naam]?.notitie;
        if (note && next[i] === undefined) {
          next[i] = note;
          next[`open_${i}`] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [schema, dagIdx, globalHistory]);

  // Bewaar actieve training zodat de sporter kan wegnavigeren en terugkeren
  useEffect(() => {
    if (!schema || finished) return;
    if (Object.keys(sets).length === 0) return;
    const dagNaamActief = schema.dagen?.[dagIdx]?.naam || "Training";
    saveActiveTraining(window.location.pathname, dagNaamActief, startTimeRef, { sets, notities, vervangingen });
  }, [sets, notities, vervangingen, schema, dagIdx, finished, startTimeRef]);

  const toggleSet = useCallback((exIdx, setIdx, rustSeconds, exerciseName, newKg, isConditie = false) => {
    unlockAudio(); // iOS: ontgrendel AudioContext bij gebruikersinteractie
    setSets(prev => {
      const updated = { ...prev };
      const exSets = [...(updated[exIdx] || [])];
      const wasDone = exSets[setIdx]?.done;
      exSets[setIdx] = { ...exSets[setIdx], done: !wasDone };
      updated[exIdx] = exSets;
      if (!wasDone) {
        // Doorswissel geluidje bij conditie-oefeningen
        if (isConditie) playDoorswissel();
        if (setIdx < exSets.length - 1 && rustSeconds > 0) {
          setRestTimer({ endTime: Date.now() + rustSeconds * 1000, total: rustSeconds });
        }
        // PR check: nieuw record als gewicht hoger is dan vorige max
        if (exerciseName && newKg > 0) {
          const histMax  = globalHistoryRef.current[exerciseName]?.kg || 0;
          const sesMax   = sessionMaxKg.current[exerciseName] || 0;
          const prevMax  = Math.max(histMax, sesMax);
          if (newKg > prevMax) {
            setPrMelding({ naam: exerciseName, kg: newKg });
            // Voeg toe aan sessie-PR lijst (dedupliceer per oefening)
            setSessionPrs(prev => {
              const idx = prev.findIndex(p => p.naam === exerciseName);
              if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = { naam: exerciseName, kg: newKg };
                return updated;
              }
              return [...prev, { naam: exerciseName, kg: newKg }];
            });
          }
          if (newKg > sesMax) {
            sessionMaxKg.current[exerciseName] = newKg;
          }
        }
      }
      return updated;
    });
  }, []);

  const updateSet = useCallback((exIdx, setIdx, field, value) => {
    setSets(prev => {
      const updated = { ...prev };
      const exSets = [...(updated[exIdx] || [])];
      exSets[setIdx] = { ...exSets[setIdx], [field]: value };
      updated[exIdx] = exSets;
      return updated;
    });
  }, []);

  // Auto-fill gewicht naar volgende lege sets bij verlaten veld
  const handleWeightBlur = useCallback((exIdx, setIdx, value) => {
    if (!value || !String(value).trim()) return;
    setSets(prev => {
      const updated = { ...prev };
      const exSets = [...(updated[exIdx] || [])];
      let changed = false;
      for (let i = setIdx + 1; i < exSets.length; i++) {
        if (!exSets[i].weight || String(exSets[i].weight).trim() === "") {
          exSets[i] = { ...exSets[i], weight: value };
          changed = true;
        }
      }
      if (changed) updated[exIdx] = [...exSets];
      return updated;
    });
  }, []);

  const allDone = Object.values(sets).every(s => s.every(r => r.done));

  const toggleSupersetRound = useCallback((exIdx, numSubEx, setIdx, rustSec) => {
    setSets(prev => {
      const allCurrentlyDone = Array.from({ length: numSubEx }, (_, si) =>
        prev[`${exIdx}-${si}`]?.[setIdx]?.done
      ).every(Boolean);
      const updated = { ...prev };
      Array.from({ length: numSubEx }, (_, si) => {
        const key = `${exIdx}-${si}`;
        const exSets = [...(updated[key] || [])];
        if (exSets[setIdx]) {
          exSets[setIdx] = { ...exSets[setIdx], done: !allCurrentlyDone };
          updated[key] = exSets;
        }
      });
      if (!allCurrentlyDone && rustSec > 0) {
        setTimeout(() => setRestTimer({ endTime: Date.now() + rustSec * 1000, total: rustSec }), 0);
      }
      return updated;
    });
  }, []);
  const dag = schema?.dagen?.[dagIdx];
  // Ondersteuning voor zowel 'dag_naam' (B2C) als 'naam' (bedrijfsschema's)
  const dagNaam = dag?.dag_naam || dag?.naam || dag?.dag || `Dag ${dagIdx + 1}`;

  // Oefeningen met tijdens-de-training aanpassingen toegepast
  const oefeningenActief = (dag?.oefeningen || []).map((ex, i) =>
    vervangingen[i] ? { ...ex, ...vervangingen[i] } : ex
  );

  const handleVervang = (exIdx, nieuw) => {
    setVervangingen(prev => ({ ...prev, [exIdx]: nieuw }));
    // Reset sets voor deze oefening op basis van nieuwe sets/reps
    const numSets = nieuw.sets ? parseInt(nieuw.sets) || 3 : 3;
    const defaultReps = nieuw.reps ? parseInt(String(nieuw.reps).match(/\d+/)?.[0]) || 10 : 10;
    setSets(prev => ({
      ...prev,
      [exIdx]: Array.from({ length: numSets }, () => ({ reps: defaultReps, weight: "", done: false })),
    }));
    setShowVervangFor(null);
  };

  const handlePermanentOpslaan = async () => {
    setPermanentStatus("saving");
    try {
      const r = await fetch(`${API}/api/my-schema-overrides/${schemaId}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });
      const cur = r.ok ? (await r.json()).overrides || {} : {};
      Object.entries(vervangingen).forEach(([i, ov]) => { cur[`${dagIdx}:${i}`] = ov; });
      const w = await fetch(`${API}/api/my-schema-overrides/${schemaId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${effectiveToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ overrides: cur }),
      });
      setPermanentStatus(w.ok ? "saved" : null);
      if (w.ok) setTimeout(() => setPermanentVraag(false), 1500);
    } catch {
      setPermanentStatus(null);
    }
  };

  const handleFinish = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(false);
    const durationSeconds = Math.round((Date.now() - startTimeRef) / 1000);
    setElapsed(durationSeconds);

    // Bouw exercises array op: supersets uitgevouwen als losse oefeningen met superset_groep
    const exercises = oefeningenActief.flatMap((ex, exIdx) => {
      if (ex.type === "superset") {
        return (ex.oefeningen || []).map((subEx, si) => ({
          code: subEx.id || `ss-${exIdx}-${si}`,
          naam: subEx.naam,
          superset_groep: String(exIdx),
          sets: (sets[`${exIdx}-${si}`] || []).map(s => ({
            kg: s.weight ? parseFloat(s.weight) || null : null,
            reps: s.reps || null,
            completed: s.done,
          })),
        }));
      }
      const isConditie = ex.spiergroep === "Conditie" || ex.type === "conditie";
      return [{
        code: ex.id || `ex-${exIdx}`,
        naam: ex.naam,
        spiergroep: ex.spiergroep || null,
        notitie: notities[exIdx]?.trim() ? notities[exIdx].trim() : null,
        sets: (sets[exIdx] || []).map(s => ({
          // Conditie: resultaat als tekst opslaan (tijd / afstand / cal)
          // Kracht: kg als getal
          kg: isConditie ? null : (s.weight ? parseFloat(s.weight) || null : null),
          resultaat: isConditie ? (s.weight || null) : null,
          reps: s.reps || null,
          completed: s.done,
        })),
      }];
    });
    setSavedExercises(exercises);

    try {
      const r = await fetch(`${API}/api/workout/${schemaId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${effectiveToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: stableSessionId,
          session_naam: dagNaam,
          training_naam: `${schema?.naam || "Custom schema"} — ${dagNaam}`,
          duration_seconds: durationSeconds,
          exercises,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        console.error("Workout opslaan mislukt:", err);
        setSaveError(true);
        setSaving(false);
        return;
      }
      setFinished(true);
      clearActiveTraining();
      if (Object.keys(vervangingen).length > 0) setPermanentVraag(true);
    } catch (e) {
      console.error("Netwerk fout:", e);
      setSaveError(true);
    }
    setSaving(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <div className="text-zinc-600 text-sm">Laden...</div>
    </div>
  );

  if (!schema || !dag) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center text-center px-4">
      <div>
        <p className="text-zinc-500 text-sm mb-4">Training niet gevonden</p>
        <button onClick={() => navigate(`/custom-schema/${schemaId}`)} className="text-xs text-[#C9A84C] underline">Terug naar schema</button>
      </div>
    </div>
  );

  if (finished) return (
    <>
      <CustomCompletionScreen
        elapsed={elapsed}
        dagNaam={dagNaam}
        schemaId={schemaId}
        stableSessionId={stableSessionId}
        savedExercises={savedExercises}
        userToken={effectiveToken}
        sessionPrs={sessionPrs}
        onBack={() => kioskToken ? navigate(-1) : navigate(`/custom-schema/${schemaId}`)}
        onAccount={() => kioskToken ? navigate(-1) : navigate("/account")}
      />
      {permanentVraag && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-5">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
          <div className="relative w-full max-w-sm rounded-2xl p-6"
            style={{ background: "#111", border: "1px solid rgba(201,168,76,0.25)" }}
            data-testid="permanent-vraag-modal">
            {permanentStatus === "saved" ? (
              <div className="text-center py-4">
                <CheckCircle size={32} className="mx-auto mb-3" style={{ color: GOLD }} />
                <p className="text-white font-bold">Schema permanent aangepast!</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <RefreshCw size={16} style={{ color: GOLD }} />
                  <p className="text-white font-bold text-base">Schema aangepast</p>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed mb-2">
                  Je hebt tijdens deze training {Object.keys(vervangingen).length === 1 ? "een oefening" : `${Object.keys(vervangingen).length} oefeningen`} aangepast:
                </p>
                <div className="space-y-1 mb-5">
                  {Object.entries(vervangingen).map(([i, ov]) => (
                    <p key={i} className="text-xs text-zinc-300">
                      <span style={{ color: GOLD }} className="font-semibold">{ov.naam || oefeningenActief[i]?.naam}</span>
                      {ov.vervangen_van && <span className="text-zinc-600"> (i.p.v. {ov.vervangen_van})</span>}
                      <span className="text-zinc-500"> · {ov.sets} sets × {ov.reps}</span>
                    </p>
                  ))}
                </div>
                <p className="text-zinc-400 text-sm mb-4">Wil je dit zo houden voor de volgende keer?</p>
                <div className="space-y-2">
                  <button onClick={handlePermanentOpslaan} disabled={permanentStatus === "saving"}
                    data-testid="permanent-ja-btn"
                    className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-br from-[#C9A84C] to-[#D4AF37] text-black disabled:opacity-60 active:scale-[0.98] transition-transform">
                    {permanentStatus === "saving" ? "Opslaan..." : "Ja, pas mijn schema permanent aan"}
                  </button>
                  <button onClick={() => setPermanentVraag(false)}
                    data-testid="permanent-nee-btn"
                    className="w-full py-3 rounded-xl font-semibold text-sm bg-white/5 border border-white/10 text-zinc-300 active:scale-[0.98] transition-transform">
                    Nee, alleen voor deze keer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-[#080808] pb-32">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#080808]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={17} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Training</p>
            <h1 className="text-white font-bold text-base truncate">{dagNaam}</h1>
          </div>
          <button
            onClick={() => setShowSchema(true)}
            data-testid="bekijk-schema-btn"
            title="Bekijk schema"
            className="w-9 h-9 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-[#C9A84C] hover:border-[#C9A84C]/40 transition-colors shrink-0"
          >
            <ClipboardList size={16} />
          </button>
          <span className="text-zinc-600 text-xs shrink-0">{dag.oefeningen?.length || 0} oef.</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {oefeningenActief.map((ex, exIdx) => {
          const rustSec = parseRustSeconds(ex.rust);
          const isConditie = ex.spiergroep === "Conditie" || ex.type === "conditie";

          // ── Superset rendering ─────────────────────────────────────────────
          if (ex.type === "superset") {
            const numSubEx = (ex.oefeningen || []).length;
            const numSets = ex.oefeningen[0]?.sets ? parseInt(ex.oefeningen[0].sets) || 3 : 3;
            const allSupersetDone = ex.oefeningen.every((_, si) =>
              (sets[`${exIdx}-${si}`] || []).every(s => s.done)
            );
            return (
              <div key={exIdx} className={`rounded-2xl border transition-all overflow-hidden ${allSupersetDone ? "border-[#C9A84C]/30 bg-[#C9A84C]/4" : "border-[#C9A84C]/20 bg-[#0f0f0f]"}`}>
                {/* Header */}
                <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "rgba(201,168,76,0.15)", background: "rgba(201,168,76,0.06)" }}>
                  <Link size={14} style={{ color: GOLD }} />
                  <span className="text-xs font-black uppercase tracking-widest" style={{ color: GOLD }}>Superset</span>
                  <span className="text-zinc-400 text-xs ml-1 truncate">{ex.oefeningen.map(o => o.naam).join(" + ")}</span>
                  {ex.rust && <span className="ml-auto text-zinc-600 text-xs shrink-0">{ex.rust} rust</span>}
                </div>

                {/* Set rondes */}
                <div className="px-3 py-3 space-y-3">
                  {Array.from({ length: numSets }).map((_, setIdx) => {
                    const roundDone = ex.oefeningen.every((_, si) =>
                      sets[`${exIdx}-${si}`]?.[setIdx]?.done
                    );
                    return (
                      <div key={setIdx} className={`rounded-xl p-3 border transition-all ${roundDone ? "border-[#C9A84C]/20 bg-[#C9A84C]/6" : "border-white/5 bg-white/2"}`}>
                        <div className="flex items-center gap-2 mb-2.5">
                          <span className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Set {setIdx + 1}</span>
                          {roundDone && <Check size={11} style={{ color: GOLD }} />}
                        </div>
                        {ex.oefeningen.map((subEx, si) => {
                          const subKey = `${exIdx}-${si}`;
                          const subSet = (sets[subKey] || [])[setIdx] || { reps: 10, weight: "", done: false };
                          return (
                            <div key={si} className="flex items-center gap-2 mb-1.5">
                              <p className="text-zinc-400 text-xs font-semibold w-24 shrink-0 truncate">{subEx.naam}</p>
                              <div className="flex items-center gap-0.5 shrink-0">
                                <button onPointerDown={e => { e.preventDefault(); updateSet(subKey, setIdx, "reps", Math.max(0, (subSet.reps || 0) - 1)); }}
                                  className="w-6 h-7 rounded-lg bg-white/6 text-zinc-400 flex items-center justify-center text-sm font-black">−</button>
                                <span className="w-7 text-center text-white font-bold text-sm tabular-nums">{subSet.reps}</span>
                                <button onPointerDown={e => { e.preventDefault(); updateSet(subKey, setIdx, "reps", (subSet.reps || 0) + 1); }}
                                  className="w-6 h-7 rounded-lg bg-white/6 text-zinc-400 flex items-center justify-center text-sm font-black">+</button>
                              </div>
                              <input type="number" inputMode="decimal" step="0.5"
                                value={subSet.weight}
                                onChange={e => updateSet(subKey, setIdx, "weight", e.target.value)}
                                onBlur={e => handleWeightBlur(subKey, setIdx, e.target.value)}
                                placeholder="kg"
                                data-testid={`superset-kg-${exIdx}-${si}-${setIdx}`}
                                className="w-16 bg-white/5 border border-white/8 rounded-lg px-2 py-1.5 text-white text-xs text-center focus:outline-none focus:border-[#C9A84C]/50"
                              />
                            </div>
                          );
                        })}
                        <button
                          onPointerDown={e => e.preventDefault()}
                          onClick={() => toggleSupersetRound(exIdx, numSubEx, setIdx, rustSec)}
                          data-testid={`superset-check-${exIdx}-${setIdx}`}
                          className={`w-full mt-2 py-2 rounded-xl font-bold text-xs transition-all ${roundDone ? "bg-[#C9A84C] text-black" : "bg-white/6 text-zinc-400 hover:bg-white/10"}`}
                        >
                          {roundDone ? "✓ Ronde voltooid" : `Set ${setIdx + 1} klaar`}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          // ── Conditie oefening rendering ────────────────────────────────────
          if (isConditie) {
            const { label, placeholder, inputMode, isTime: isTimeType } = parseConditieType(ex.reps);
            const targetSec = isTimeType ? parseTargetSeconds(ex.reps) : null;
            const exSetsC = sets[exIdx] || [];
            const allSetsDoneC = exSetsC.every(s => s.done);
            const circumference = 2 * Math.PI * 28;

            return (
              <div key={exIdx} className={`rounded-2xl border transition-all ${allSetsDoneC ? "border-cyan-500/30 bg-cyan-500/5" : "border-cyan-500/15 bg-[#0a0f10]"}`}>
                {/* Header */}
                <div className="px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${allSetsDoneC ? "bg-cyan-500 text-black" : "bg-cyan-500/10 text-cyan-400"}`}>
                      {allSetsDoneC ? <Check size={16} /> : <Activity size={15} />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-bold text-base leading-tight">{ex.naam}</p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-black uppercase tracking-widest bg-cyan-500/10 text-cyan-400">conditie</span>
                      </div>
                      <p className="text-zinc-500 text-xs mt-0.5">
                        {ex.sets} sets · Doel: <span className="text-cyan-600 font-semibold">{ex.reps}</span>
                        {ex.rust ? ` · ${ex.rust} rust` : ""}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setShowInfoFor(exIdx)}
                    data-testid={`info-btn-custom-${exIdx}`}
                    className="w-8 h-8 rounded-lg flex items-center justify-center active:scale-90"
                    style={{ background: "rgba(34,211,238,0.07)" }}>
                    <Info size={14} style={{ color: "rgba(34,211,238,0.5)" }} />
                  </button>
                </div>

                {/* Set rijen */}
                <div className="px-3 pb-4 space-y-2">
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-1 px-1">
                    <span className="w-4 text-center shrink-0">#</span>
                    <span className="w-16 text-center shrink-0 text-cyan-700">Doel</span>
                    <span className="flex-1 text-center text-cyan-700">{label}</span>
                    {isTimeType && <span className="w-8 shrink-0" />}
                    <span className="w-8 text-center shrink-0">✓</span>
                  </div>

                  {exSetsC.map((set, setIdx) => {
                    const timerActive =
                      activeConditieTimer?.exIdx === exIdx &&
                      activeConditieTimer?.setIdx === setIdx;
                    const remaining = timerActive
                      ? Math.max(0, Math.ceil((activeConditieTimer.endTime - Date.now()) / 1000))
                      : null;
                    const progressPct = timerActive ? remaining / activeConditieTimer.total : 0;

                    if (timerActive) {
                      return (
                        <div key={setIdx}
                          className="flex items-center gap-3 rounded-2xl px-3 py-3 bg-cyan-500/10 border border-cyan-400/30"
                          style={{ minHeight: "72px" }}>
                          <span className="w-4 shrink-0 text-cyan-500 text-xs font-black text-center">{setIdx + 1}</span>
                          <div className="flex-1 flex items-center justify-center">
                            <div className="relative w-14 h-14">
                              <svg className="absolute inset-0 -rotate-90 w-full h-full" viewBox="0 0 70 70">
                                <circle cx="35" cy="35" r="28" fill="none"
                                  stroke="rgba(34,211,238,0.12)" strokeWidth="5" />
                                <circle cx="35" cy="35" r="28" fill="none"
                                  stroke="rgb(34,211,238)" strokeWidth="5"
                                  strokeDasharray={circumference}
                                  strokeDashoffset={circumference * progressPct}
                                  strokeLinecap="round"
                                  style={{ transition: "stroke-dashoffset 0.1s linear" }} />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center text-cyan-400 font-black text-lg tabular-nums leading-none">
                                {formatSeconds(remaining)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => setActiveConditieTimer(null)}
                            data-testid={`stop-conditie-timer-${exIdx}-${setIdx}`}
                            className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center bg-white/8 text-zinc-400 active:scale-90 transition-all hover:bg-white/12">
                            <Square size={13} />
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div key={setIdx}
                        className={`flex items-center gap-2 rounded-xl px-2 py-1.5 transition-all ${set.done ? "bg-cyan-500/8 border border-cyan-500/20" : "bg-white/3 border border-transparent"}`}>
                        <span className="w-4 shrink-0 text-zinc-500 text-xs font-bold text-center">{setIdx + 1}</span>
                        <span className="w-16 shrink-0 text-cyan-600/80 text-xs font-semibold text-center">{ex.reps}</span>
                        {set.done ? (
                          <span className="flex-1 text-center text-cyan-400 font-black text-base tabular-nums">{set.weight || "—"}</span>
                        ) : (
                          <input
                            type={inputMode === "text" ? "text" : "number"}
                            inputMode={inputMode}
                            placeholder={placeholder}
                            value={set.weight}
                            onChange={e => updateSet(exIdx, setIdx, "weight", e.target.value)}
                            data-testid={`conditie-result-${exIdx}-${setIdx}`}
                            className="flex-1 min-w-0 bg-[#071213] border border-cyan-900/40 rounded-xl py-2 text-white text-base text-center font-black focus:outline-none focus:border-cyan-500/60 focus:bg-[#041415] tabular-nums transition-colors placeholder:text-zinc-700"
                          />
                        )}
                        {isTimeType && !set.done && targetSec && (
                          <button
                        onClick={() => {
                            unlockAudio(); // iOS: ontgrendel vóór timer start
                            setActiveConditieTimer({ exIdx, setIdx, endTime: Date.now() + targetSec * 1000, total: targetSec });
                          }}
                            data-testid={`start-conditie-timer-${exIdx}-${setIdx}`}
                            className="w-8 h-9 rounded-lg shrink-0 flex items-center justify-center bg-cyan-500/15 text-cyan-400 active:scale-90 transition-all hover:bg-cyan-500/25">
                            <Play size={11} fill="currentColor" />
                          </button>
                        )}
                        <button
                          onClick={() => toggleSet(exIdx, setIdx, rustSec, ex.naam, 0, true)}
                          data-testid={`toggle-conditie-set-${exIdx}-${setIdx}`}
                          className={`w-8 h-9 rounded-lg shrink-0 flex items-center justify-center transition-all active:scale-90 ${set.done ? "text-black bg-cyan-500" : "bg-white/5 text-zinc-600"}`}>
                          {set.done ? <Check size={13} /> : <X size={11} />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          // ── Normale oefening rendering ─────────────────────────────────────
          const exSets = sets[exIdx] || [];
          const allSetsDone = exSets.every(s => s.done);

          return (
            <div key={exIdx} className={`rounded-2xl border transition-all ${allSetsDone ? "border-[#C9A84C]/30 bg-[#C9A84C]/5" : "border-white/6 bg-[#0f0f0f]"}`}>
              {/* Oefening header */}
              <div className="px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${allSetsDone ? "bg-[#C9A84C] text-black" : "bg-white/5 text-zinc-500"}`}>
                    {allSetsDone ? <Check size={16} /> : <Dumbbell size={15} />}
                  </div>
                  <div>
                    <p className="text-white font-bold text-base">{ex.naam}</p>
                    <p className="text-zinc-500 text-xs mt-0.5">{ex.sets} sets · {ex.reps} reps{ex.rust ? ` · ${ex.rust} rust` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {ex.gewicht && <span className="text-zinc-500 text-xs">{ex.gewicht}</span>}
                  <button
                    onClick={() => setShowVervangFor(exIdx)}
                    data-testid={`vervang-btn-custom-${exIdx}`}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90"
                    style={{ background: vervangingen[exIdx] ? "rgba(201,168,76,0.2)" : "rgba(201,168,76,0.08)" }}
                    title="Oefening aanpassen/vervangen"
                  >
                    <RefreshCw size={13} style={{ color: vervangingen[exIdx] ? GOLD : "rgba(201,168,76,0.6)" }} />
                  </button>
                  <button
                    onClick={() => setNotities(p => ({ ...p, [`open_${exIdx}`]: !p[`open_${exIdx}`] }))}
                    data-testid={`note-btn-custom-${exIdx}`}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90"
                    style={{ background: notities[exIdx]?.trim() ? "rgba(201,168,76,0.2)" : "rgba(201,168,76,0.08)" }}
                    title="Opmerking toevoegen"
                  >
                    <FileText size={14} style={{ color: notities[exIdx]?.trim() ? GOLD : "rgba(201,168,76,0.6)" }} />
                  </button>
                  <button
                    onClick={() => setShowInfoFor(exIdx)}
                    data-testid={`info-btn-custom-${exIdx}`}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90"
                    style={{ background: "rgba(201,168,76,0.08)" }}
                    title="Uitvoering bekijken"
                  >
                    <Info size={14} style={{ color: "rgba(201,168,76,0.6)" }} />
                  </button>
                </div>
              </div>

              {/* Opmerking bij oefening (zichtbaar voor coach/admin) */}
              {notities[`open_${exIdx}`] && (
                <div className="px-4 pb-2">
                  <textarea
                    value={notities[exIdx] || ""}
                    onChange={e => setNotities(p => ({ ...p, [exIdx]: e.target.value }))}
                    placeholder="Opmerking bij deze oefening (bijv. voelde zwaar, schouder gevoelig)..."
                    rows={2}
                    data-testid={`note-input-custom-${exIdx}`}
                    className="w-full bg-[#111] border border-white/8 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 resize-none placeholder-zinc-600"
                  />
                </div>
              )}

              {/* Sets */}
              <div className="px-3 pb-4 space-y-1.5">
                {/* Kolomhoofden */}
                {(() => {
                  const hasPrevHeader = prevLog?.exercises?.some(
                    pe => exSets.length > 0 && ((ex.id && pe.code === ex.id) || pe.naam === ex.naam) && pe.sets?.some(s => s.kg != null)
                  ) || !!globalHistory[ex.naam];
                  return (
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-1 px-1">
                      <span className="w-4 text-center shrink-0">#</span>
                      {hasPrevHeader && <span className="w-14 text-center shrink-0">Vorig</span>}
                      <span className="w-16 text-center shrink-0">Reps</span>
                      <span className="flex-1 text-center">kg</span>
                      <span className="w-8 text-center shrink-0">✓</span>
                    </div>
                  );
                })()}

                {exSets.map((set, setIdx) => {
                  const prevEx = prevLog?.exercises?.find(
                    pe => (ex.id && pe.code === ex.id) || pe.naam === ex.naam
                  );
                  const ps = prevEx?.sets?.[setIdx];
                  // prevLog heeft prioriteit; globale history als fallback
                  const globalPrev = !prevEx ? globalHistory[ex.naam] : null;
                  const prevText = ps?.kg != null
                    ? `${ps.kg}×${ps.reps ?? "–"}`
                    : globalPrev?.kg != null ? `${globalPrev.kg}×${globalPrev.reps ?? "–"}`
                    : null;
                  const hasPrev = !!prevText;

                  return (
                    <div key={setIdx} className={`flex items-center gap-1.5 rounded-xl px-2 py-1.5 transition-all ${set.done ? "bg-[#C9A84C]/8 border border-[#C9A84C]/15" : "bg-white/3 border border-transparent"}`}>
                      {/* Set nummer */}
                      <span className="w-4 shrink-0 text-zinc-500 text-xs font-bold text-center">{setIdx + 1}</span>

                      {/* Vorig (toon als er data is) */}
                      {hasPrev && (
                        <span className="w-14 shrink-0 text-zinc-400 text-xs font-semibold text-center tabular-nums truncate">
                          {prevText || "–"}
                        </span>
                      )}

                      {/* Reps stepper: compact */}
                      <div className="flex items-center gap-0.5 shrink-0">
                        <button
                          onPointerDown={e => { e.preventDefault(); updateSet(exIdx, setIdx, "reps", Math.max(0, set.reps - 1)); }}
                          className="w-6 h-8 rounded-lg bg-white/6 text-zinc-400 active:text-white flex items-center justify-center text-sm font-black select-none"
                          data-testid={`reps-minus-${exIdx}-${setIdx}`}
                        >−</button>
                        <span
                          className="w-6 text-center text-white font-black text-sm tabular-nums select-none"
                          data-testid={`reps-value-${exIdx}-${setIdx}`}
                        >{set.reps}</span>
                        <button
                          onPointerDown={e => { e.preventDefault(); updateSet(exIdx, setIdx, "reps", set.reps + 1); }}
                          className="w-6 h-8 rounded-lg bg-white/6 text-zinc-400 active:text-white flex items-center justify-center text-sm font-black select-none"
                          data-testid={`reps-plus-${exIdx}-${setIdx}`}
                        >+</button>
                      </div>

                      {/* KG stepper: neemt alle resterende ruimte */}
                      <div className="flex-1 flex items-center gap-0.5 min-w-0">
                        <button
                          onPointerDown={e => { e.preventDefault(); updateSet(exIdx, setIdx, "weight", String(Math.max(0, Math.round(((parseFloat(set.weight) || 0) - 2.5) * 10) / 10))); }}
                          className="w-7 h-9 rounded-lg flex items-center justify-center text-base font-black select-none shrink-0 transition-all active:scale-90"
                          style={{ background: "rgba(255,255,255,0.07)", color: "#71717a" }}
                          data-testid={`weight-minus-${exIdx}-${setIdx}`}
                        >−</button>
                        <input
                          type="number"
                          inputMode="decimal"
                          placeholder={prevText || "0"}
                          value={set.weight}
                          onChange={e => updateSet(exIdx, setIdx, "weight", e.target.value)}
                          onBlur={e => handleWeightBlur(exIdx, setIdx, e.target.value)}
                          className="flex-1 min-w-0 bg-[#111] border border-white/8 rounded-xl py-2 text-white text-base text-center font-black focus:outline-none focus:border-[#C9A84C]/60 focus:bg-[#0f0c02] tabular-nums transition-colors"
                          data-testid={`weight-input-${exIdx}-${setIdx}`}
                        />
                        <button
                          onPointerDown={e => { e.preventDefault(); updateSet(exIdx, setIdx, "weight", String(Math.round(((parseFloat(set.weight) || 0) + 2.5) * 10) / 10)); }}
                          className="w-7 h-9 rounded-lg flex items-center justify-center text-base font-black select-none shrink-0 transition-all active:scale-90"
                          style={{ background: "rgba(255,255,255,0.07)", color: "#71717a" }}
                          data-testid={`weight-plus-${exIdx}-${setIdx}`}
                        >+</button>
                      </div>

                      {/* Klaar knop */}
                      <button
                        onClick={() => toggleSet(exIdx, setIdx, rustSec, ex.naam, parseFloat(sets[exIdx]?.[setIdx]?.weight || "0") || 0, isConditie)}
                        data-testid={`toggle-set-${exIdx}-${setIdx}`}
                        className={`w-8 h-9 rounded-lg shrink-0 flex items-center justify-center transition-all active:scale-90 ${set.done ? "text-black" : "bg-white/5 text-zinc-600"}`}
                        style={set.done ? { background: GOLD } : {}}
                      >
                        {set.done ? <Check size={13} /> : <X size={11} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Afronding knop */}
        <div className="pt-4">
          <button
            onClick={handleFinish}
            disabled={saving}
            data-testid="finish-workout-btn"
            className={`w-full py-4 rounded-2xl font-black text-base uppercase tracking-wide transition-all flex items-center justify-center gap-2
              ${allDone ? "text-black shadow-lg active:scale-95" : "bg-white/5 text-zinc-500 cursor-default"}`}
            style={allDone ? { background: GOLD } : {}}
          >
            {saving ? "Opslaan..." : allDone ? <><CheckCircle size={18} /> Training voltooid!</> : `Nog ${Object.values(sets).flat().filter(s => !s.done).length} sets te gaan`}
          </button>
          {saveError && (
            <p className="text-center text-red-400 text-xs mt-3">Opslaan mislukt — controleer je verbinding en probeer opnieuw.</p>
          )}
          {!allDone && !saveError && (
            <p className="text-center text-zinc-700 text-xs mt-3">Voltooi alle sets om de training op te slaan</p>
          )}
        </div>
      </div>

      <RestTimer
        endTime={restTimer?.endTime}
        total={restTimer?.total}
        onSkip={() => setRestTimer(null)}
      />

      <PrMelding melding={prMelding} onDismiss={() => setPrMelding(null)} />

      {showInfoFor !== null && oefeningenActief[showInfoFor] && (
        <ExerciseInfoModal
          ex={oefeningenActief[showInfoFor]}
          token={effectiveToken}
          huidigeSets={sets[showInfoFor] || []}
          onClose={() => setShowInfoFor(null)}
        />
      )}

      {showVervangFor !== null && oefeningenActief[showVervangFor] && (
        <VervangOefeningModal
          ex={oefeningenActief[showVervangFor]}
          token={effectiveToken}
          onClose={() => setShowVervangFor(null)}
          onSave={(nieuw) => handleVervang(showVervangFor, nieuw)}
        />
      )}

      {showSchema && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center" onClick={() => setShowSchema(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg rounded-t-3xl max-h-[85vh] flex flex-col"
            style={{ background: "#111", border: "1px solid rgba(201,168,76,0.2)", borderBottom: "none" }}
            onClick={e => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-4 border-b border-white/6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardList size={16} style={{ color: GOLD }} />
                <div>
                  <p className="text-white font-bold text-sm">Jouw schema</p>
                  <p className="text-zinc-500 text-xs">{dagNaam} · {dag.oefeningen?.length || 0} oefeningen</p>
                </div>
              </div>
              <button onClick={() => setShowSchema(false)} data-testid="schema-overzicht-sluiten" className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3 pb-8">
              {oefeningenActief.map((ex, i) => {
                const namen = ex.type === "superset" ? (ex.oefeningen || []).map(o => o.naam).join(" + ") : ex.naam;
                return (
                  <div key={i} className="rounded-xl border border-white/6 p-4" style={{ background: "#0d0d0d" }}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-black shrink-0" style={{ background: "rgba(201,168,76,0.15)", color: GOLD }}>{i + 1}</span>
                      <p className="text-white font-bold text-sm">{ex.type === "superset" ? "Superset" : namen}</p>
                    </div>
                    {ex.type === "superset" && <p className="text-zinc-400 text-xs pl-7 mb-1">{namen}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400 pl-7">
                      {ex.sets && <span><span className="text-zinc-600">Sets:</span> {ex.sets}</span>}
                      {ex.reps && <span><span className="text-zinc-600">Reps:</span> {ex.reps}</span>}
                      {ex.rust && <span><span className="text-zinc-600">Rust:</span> {ex.rust}</span>}
                    </div>
                    {(ex.notities || ex.instructie) && (
                      <p className="text-zinc-400 text-xs leading-relaxed mt-2 pl-7 italic">{ex.notities || ex.instructie}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
